import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IGeneratePdfRq } from '@smarttypes/api';
import {
  CommunicationChannelEnum,
  ICustomWidget,
  ICustomWidgetV1Widget,
  IVaWidget,
  IVaWidgetCategory,
  IVisitorsArea,
  VaWidgetCode,
} from '@smarttypes/core';
import { IGuest } from '@smarttypes/hotel';
import { IRegistrationCard, IRegistrationCardConfig } from '@smarttypes/registration-card';
import { ColorAccentService, WindowService } from 'angular-v2-services';
import { getAnalyticUniqueId } from 'angular-v2-utils';
import { set } from 'lodash';
import { map, Observable, shareReplay, tap } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class VisitorsAreaService {
  private _config!: IVisitorsArea;
  private configRequest$?: Observable<IVisitorsArea>;

  constructor(
    private readonly httpClient: HttpClient,
    private windowService: WindowService,
    private colorAccentService: ColorAccentService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  setLanguages(languages: string[]) {
    this._config.languages = languages;
  }

  getLanguages(): string[] {
    return this._config.languages ?? [];
  }

  pickChannel(id: string, companyId: string, channel: CommunicationChannelEnum, lang?: string, mode?: string | null) {
    let params = `id=${id}&companyId=${companyId}&channel=${channel}&lang=${lang}`;
    if (mode) {
      params += `&mode=${mode}`;
    }
    return this.httpClient.get(`/visitors-area/public/pick-channel?${params}`);
  }

  visit(id: string) {
    return this.httpClient.get(`/visitors-area/public/visit/${id}`).subscribe();
  }

  gaVisit(companyId: string, referralId: string) {
    return this.httpClient.post(`/public/metrics/ga-view`, {
      companyId,
      referralId: referralId ?? getAnalyticUniqueId(),
    });
  }

  widgetClick(widgetName: string, key = 'GA_WIDGET') {
    const name = widgetName.toUpperCase().replace(/-/g, '_');
    const url = `/public/metrics/${key}_${name}`;
    return this.httpClient
      .post(url, {
        companyId: this._config.companyId,
      })
      .subscribe();
  }

  getConfig(skipSecured = false, noSave?: boolean): Observable<IVisitorsArea> {
    if (!this.configRequest$) {
      this.configRequest$ = this.#createConfigRequest$();
    }
    return this.configRequest$.pipe(
      tap(config => {
        if (config?.secured && !skipSecured) {
          this.router.navigate(['/login']);
        }
      }),
      tap(config => {
        if (!noSave) {
          this._config = config;
          this.colorAccentService.initCustomColor(config);
        }
      }),
    );
  }

  saveRegistrationCard(
    request: IRegistrationCard & IGeneratePdfRq,
    guest?: IGuest,
    allowCreateGuest?: boolean,
  ): Observable<{ guestId: string }> {
    const body = {
      companyId: request.companyId,
      answers: request.answers,
      consentAnswers: request.consentAnswers,
      language: request.language,
      signature: request.signature,
      outputPath: request.outputPath,
      templateParams: request.templateParams,
      url: request.url,
      pageFormat: request.pageFormat,
      registrationAttachments: request.registrationAttachments,
      blockGuestCreation: !allowCreateGuest,
    };

    if (guest) {
      set(body, 'guestId', guest._id);
    }

    return this.httpClient.post<{ guestId: string }>(`/visitors-area/public/save-registration-card`, body);
  }

  getRegistrationCardConfig(companyId: string, lang: string): Observable<IRegistrationCardConfig> {
    return this.httpClient.get<IRegistrationCardConfig>(
      `/visitors-area/public/get-registration-card-config/${companyId}/${lang}`,
    );
  }

  getRegistrationCardLanguages(companyId: string): Observable<string[]> {
    return this.httpClient.get<string[]>(`/visitors-area/public/get-registration-card-languages/${companyId}`);
  }

  getIsRegistrationCardEnabled(companyId: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`/visitors-area/public/is-registration-card-enabled/${companyId}`);
  }

  sendFile(file: File, companyId: string, uploadSource: 'registration-card' | 'chat'): Observable<any> {
    let data: any;

    return this.upload(companyId, file.name, uploadSource).pipe(
      tap(rs => (data = rs)),
      filter(res => !!res?.signed),
      switchMap(
        res =>
          new Observable<{
            signed: string;
            url: string;
            blob: Blob;
          }>(subscriber => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onerror = err => {
              subscriber.error(err);
              subscriber.complete();
            };
            reader.onload = () => {
              subscriber.next({
                ...res,
                blob: this.b64toBlob(reader.result, file.type),
              });
              subscriber.complete();
            };
          }),
      ),
      switchMap((res: { signed: string; url: string; blob: Blob }) =>
        this.httpClient.put(res.signed, res.blob).pipe(map(() => res)),
      ),
    );
  }

  signUp(rq: {
    language: string;
    whatsAppNumber: string;
    companyId: string;
    smartAi?: boolean;
  }): Observable<{ guestId: string }> {
    return this.httpClient.post<{ guestId: string }>(`/wa-login-flow/sign-up`, rq);
  }

  private getDomain(): string {
    if (this.#isDev()) {
      return environment?.demoGA ?? '';
    } else {
      const host = this.windowService.location.host;
      if (host.includes('m.guestbook.smartguest.co')) {
        return 'guestbook.smartguest.co';
      }
      return host;
    }
  }

  private upload(
    companyId: string,
    fileName: string,
    uploadSource: 'registration-card' | 'chat',
  ): Observable<{ url: string; signed: string }> {
    return this.httpClient.post<{ url: string; signed: string }>(`/public/files/upload`, {
      companyId,
      fileName,
      uploadSource,
    });
  }

  private b64toBlob(b64Data: any, type = 'image/png'): Blob {
    let data;
    const aux = b64Data.split(',');
    if (aux.length > 1) {
      data = aux[1];
    } else {
      data = b64Data;
    }
    const byteString = atob(data);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i += 1) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type });
  }

  #createConfigRequest$(): Observable<IVisitorsArea> {
    let urlApi = `${environment.api}/visitors-area/public?domain=${this.getDomain()}`;
    const history = this.route.snapshot.queryParamMap.get('history');
    if (history) {
      urlApi = `${urlApi}&history=${history}`;
    }
    return this.httpClient.get<IVisitorsArea>(urlApi).pipe(
      map(config => {
        this.#redirectToSavedGuest(config);
        this.#initCustomWidgets(config);
        return config;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  #redirectToSavedGuest(config: IVisitorsArea) {
    if (this.#isDev()) {
      return;
    }
    if (
      this.getDomain() !== config.url &&
      localStorage.getItem('guestId') &&
      localStorage.getItem('guestId') !== 'preview'
    ) {
      window.location.assign(`https://${config.url}/${localStorage.getItem('guestId')}`);
    }
  }

  #isDev() {
    return this.windowService.location.host.includes('localhost');
  }

  #initCustomWidgets(config: IVisitorsArea) {
    // @TODO this is not the best idea - refactor in the future
    config.widgets = config.widgets.reduce((aggr, item) => {
      item.widgets = item.widgets.reduce((widgets, widget) => {
        if (widget.code === 'custom-widget-v1') {
          widgets.push(
            ...(((widget as IVaWidget<ICustomWidgetV1Widget>)?.data?.widgets ?? []) as ICustomWidget[])
              .filter(w => w.active)
              .map(w => {
                return Object.assign(
                  {},
                  {
                    code: VaWidgetCode.custom,
                    enabled: true,
                    category: item.category,
                    title: w.title,
                    place: w.place,
                    position: w.position,
                    type: 'box',
                    icon: w.icon,
                    data: w,
                    expandable: true,
                  },
                ) as IVaWidget;
              }),
          );
        } else {
          widgets.push(widget);
        }
        return widgets;
      }, [] as IVaWidget[]);
      aggr.push(item);
      return aggr;
    }, [] as IVaWidgetCategory[]);
  }
}
